import { useEffect, useRef, useState } from 'react'
import { Link, useSearchParams, Navigate } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { CheckCircle, Loader2, UserPlus } from 'lucide-react'
import { callEdgeFunction } from '@/lib/edge-functions'
import { useCartStore } from '@/stores/cart-store'
import { useAuth } from '@/hooks/useAuth'
import type { Order, Product } from '@/types/database'
import { getPageTitle } from '@/lib/branding'

interface OrderDetails {
  order: Order & { products: Product | null }
  items?: Array<{
    product_title: string | null
    quantity: number
    unit_price: number
    line_total: number
    variant_label: string | null
  }>
  session: {
    amount_total: number
    customer_details: {
      email: string
      name: string
      address: {
        line1: string
        line2?: string
        city: string
        state: string
        postal_code: string
        country: string
      }
    }
  }
}

export function OrderSuccessPage() {
  const [searchParams] = useSearchParams()
  const sessionId = searchParams.get('session_id')
  const clearCart = useCartStore(state => state.clearCart)
  const { user } = useAuth()
  const hasAttemptedOrderClaim = useRef(false)
  const [orderDetails, setOrderDetails] = useState<OrderDetails | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!sessionId) {
      setIsLoading(false)
      return
    }

    if (sessionId.startsWith('cs_')) {
      clearCart()
    }

    async function fetchOrderDetails() {
      const result = await callEdgeFunction<OrderDetails>('get-order-details', {
        method: 'POST',
        body: { session_id: sessionId },
      })

      if (result.error) {
        setError(result.error)
      } else {
        setOrderDetails(result.data)
      }
      setIsLoading(false)
    }

    fetchOrderDetails()
  }, [clearCart, sessionId])

  useEffect(() => {
    if (!user?.id || !orderDetails?.order?.id || hasAttemptedOrderClaim.current) {
      return
    }

    hasAttemptedOrderClaim.current = true

    async function claimOrdersForAuthenticatedUser() {
      const result = await callEdgeFunction<{ success: boolean; claimed: number }>('claim-orders', {
        method: 'POST',
        withAuth: true,
      })

      if (result.error && import.meta.env.DEV) {
        console.warn('Failed to claim guest orders after checkout success:', {
          error: result.error,
          errorCode: result.errorCode,
          status: result.status,
        })
      }
    }

    claimOrdersForAuthenticatedUser()
  }, [orderDetails?.order?.id, user?.id])

  if (!sessionId) {
    return <Navigate to="/" replace />
  }

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-20 text-center">
        <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
        <p className="mt-4 text-muted-foreground">Loading order details...</p>
      </div>
    )
  }

  if (error || !orderDetails) {
    // Still show success even if we can't fetch details
    return (
      <>
        <Helmet>
          <title>{getPageTitle('Order Confirmed')}</title>
        </Helmet>
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-2xl mx-auto text-center">
            <div className="mb-6">
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto" />
            </div>

            <h1 className="text-3xl md:text-4xl font-bold mb-4">
              Order Confirmed!
            </h1>

            <p className="text-lg text-muted-foreground mb-8">
              Thank you for your purchase. We've sent a confirmation email with your order details.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild>
                <Link to="/">Back to Home</Link>
              </Button>
              <Button variant="outline" asChild>
                <Link to="/past-reviews">Browse More Deals</Link>
              </Button>
            </div>
          </div>
        </div>
      </>
    )
  }

  const { order, items, session } = orderDetails
  const orderId = order?.id

  return (
    <>
      <Helmet>
        <title>{getPageTitle('Order Confirmed')}</title>
      </Helmet>
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-2xl mx-auto text-center">
          <div className="mb-6">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto" />
          </div>

          <h1 className="text-3xl md:text-4xl font-bold mb-4">
            Order Confirmed!
          </h1>

          <p className="text-lg text-muted-foreground mb-8">
            Thank you for your purchase. We've sent a confirmation email to{' '}
            <span className="font-medium text-foreground">
              {session.customer_details?.email}
            </span>
          </p>

          <Card className="mb-8 text-left">
            <CardHeader>
              <CardTitle>Order Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Show individual items if available */}
              {items && items.length > 0 ? (
                <div className="space-y-3">
                  {items.map((item, i) => (
                    <div key={i} className="flex justify-between text-sm">
                      <div>
                        <p className="font-medium">{item.product_title}</p>
                        {item.variant_label && (
                          <p className="text-xs text-muted-foreground">{item.variant_label}</p>
                        )}
                        {item.quantity > 1 && (
                          <p className="text-xs text-muted-foreground">Qty: {item.quantity}</p>
                        )}
                      </div>
                      <p className="font-medium">${item.line_total.toFixed(2)}</p>
                    </div>
                  ))}
                </div>
              ) : order?.products && (
                <div>
                  <p className="text-sm text-muted-foreground">Product</p>
                  <p className="font-medium">{order.products.title}</p>
                </div>
              )}

              <div>
                <p className="text-sm text-muted-foreground">Total Paid</p>
                <p className="font-medium">
                  ${((session.amount_total || 0) / 100).toFixed(2)} USD
                </p>
              </div>

              {session.customer_details?.address && (
                <div>
                  <p className="text-sm text-muted-foreground">Shipping Address</p>
                  <p className="font-medium">
                    {session.customer_details.name}
                    <br />
                    {session.customer_details.address.line1}
                    {session.customer_details.address.line2 && (
                      <>
                        <br />
                        {session.customer_details.address.line2}
                      </>
                    )}
                    <br />
                    {session.customer_details.address.city},{' '}
                    {session.customer_details.address.state}{' '}
                    {session.customer_details.address.postal_code}
                    <br />
                    {session.customer_details.address.country}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="space-y-4">
            <p className="text-muted-foreground">
              Most orders arrive within 5-7 business days. We'll send you an email with tracking information once your order ships.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {user && orderId ? (
                <Button asChild>
                  <Link to={`/account/orders/${orderId}`}>View in Account</Link>
                </Button>
              ) : (
                <Button asChild>
                  <Link to="/">Back to Home</Link>
                </Button>
              )}
              <Button variant="outline" asChild>
                <Link to="/past-reviews">Browse More Deals</Link>
              </Button>
            </div>

            {/* Guest checkout CTA */}
            {!user && (
              <Card className="mt-6 text-left">
                <CardContent className="flex items-center gap-4 py-4">
                  <UserPlus className="h-8 w-8 text-primary flex-shrink-0" />
                  <div className="flex-1">
                    <p className="font-medium">Create an account to track your orders</p>
                    <p className="text-sm text-muted-foreground">
                      Sign up to view order status, tracking info, and manage your purchases.
                    </p>
                  </div>
                  <Button variant="outline" size="sm" asChild>
                    <Link to="/?auth=signup">Sign Up</Link>
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </>
  )
}
